<template>
  <div class="notification" :class="`notification--${notification.type}`">
    <div class="notification__icon-wrapper">
      <SVGIcon
        v-if="notification.type === 'info'"
        class="notification__icon"
        iconName="info"
        alt=""
      />
      <SVGIcon
        v-else-if="notification.type === 'error'"
        class="notification__icon"
        iconName="alert-triangle-etc"
        alt=""
      />
    </div>
    <div class="notification__text-wrapper">
      <div v-if="notification.title !== undefined" class="notification__title">
        {{ notification.title }}
      </div>
      <div
        v-if="notification.message !== undefined"
        class="notification__message"
      >
        {{ notification.message }}
      </div>
    </div>
    <div
      v-if="notification.dismissible"
      class="notification__close-button-wrapper"
    >
      <SVGIcon
        class="notification__close-button"
        iconName="x-etc"
        alt="X"
        @click="closeNotification(notification.id)"
      />
    </div>
    <div
      class="notification__progress"
      :style="`animation: ${progressAnimation};`"
    ></div>
  </div>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { computed, defineComponent, toRefs } from "vue";
import type { Notification } from "@/common/types/notification";
import { useNotifications } from "@/common/notification";
import { SVGIcon } from "et-components";

export default defineComponent({
  components: { SVGIcon },
  props: {
    notification: {
      required: true,
      type: Object as PropType<Notification>,
    },
  },
  setup(props) {
    const { notification } = toRefs(props);

    const progressAnimation = computed(
      () => `reverse-progress-bar ${notification.value.timeout}ms linear`,
    );

    const closeNotification = useNotifications().remove;

    return { progressAnimation, closeNotification };
  },
});
</script>

<style scoped lang="scss">
$progress-bar-height: 4px;

.notification {
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;

  box-shadow: 0 0 5px $color-etGrey;

  padding-bottom: $progress-bar-height;

  &--info {
    color: white;
    background-color: $color-etBlue;
  }

  &--error {
    color: white;
    background-color: $color-etRed;
  }
}

/* Icon */

.notification__icon-wrapper {
  flex-grow: 0;
  flex-shrink: 0;
}

.notification__icon {
  display: block;

  margin: 8px 16px 8px 8px;

  width: 28px;
  height: 28px;
}

/* Text */

.notification__text-wrapper {
  flex: 1 1 100%;

  margin: 4px 0;
}

.notification__title {
  font-weight: bold;
  margin-bottom: 4px;
}

/* Close Button */

.notification__close-button-wrapper {
  flex-grow: 0;
  flex-shrink: 0;
  align-self: flex-start;
}

.notification__close-button {
  @include image-button(12px);
  margin: 4px;
}

/* Progress Bar */

.notification__progress {
  position: absolute;
  bottom: 0;
  left: 0;

  height: $progress-bar-height;

  background-color: white;
}
</style>

<style lang="scss">
// keyframes may not be defined in scoped style because they would get a random id
// and then couldn't be accessed from the inline animation style applied in the template
@keyframes reverse-progress-bar {
  0% {
    width: 100%;
  }
  100% {
    width: 0;
  }
}
</style>
